/**
 * Placement in millimetres
 *
 * The stored placement of a design on a print area (`DesignPosition.
 * placementMm`): its axis-aligned box in millimetres relative to the top
 * left corner of the area (R17). Templates come and go, the box does not:
 * the canvas pixels of the active template follow from the template's
 * Pixel Scale (R18), the relative view the carry-over rule works on follows
 * from the area's millimetres (R36).
 *
 * Pure conversions only; no placement rule lives here.
 */

import type { RelativeDesignState } from '../design/placementTypes.js';
import {
  effectiveCarryOver,
  type CarryOverFields,
} from '../design/carryOverDefaults.js';
import { isFinitePositive } from '../print/templateGeometry.js';
import type { PlacementAreaProduct } from './placementAreaOf.js';
import {
  effectiveSizeTier,
  type PlacementArea,
  type SizeTier,
} from './sizeTier.js';

/** A design's box on a print area, millimetres from the area's top left. */
export interface PlacementMm {
  x: number;
  y: number;
  width: number;
  height: number;
  /**
   * Metadata of a single rotated object (importers deliver an angle). Not
   * part of any transformation: the objects carry their own angles.
   */
  rotation: number;
}

/** A print area as the placement rules see it: physical size and tier. */
export interface PlacementAreaMm extends Pick<
  PlacementArea,
  | 'productId'
  | 'productClass'
  | 'carryOverPercent'
  | 'carryOverAnchor'
  | 'carryOverTopOffsetMm'
> {
  widthMm: number;
  heightMm: number;
  sizeTier?: SizeTier | null;
  sizeTierOverride?: SizeTier | null;
}

/** The same box in canvas pixels of one template, relative to the area. */
export interface CanvasRectPx {
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;
}

/** Millimetres two placements may differ before they count as different (R35). */
export const PLACEMENT_MM_TOLERANCE = 0.1;

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

/** Whether a stored JSON value is a usable placement. */
export function isPlacementMm(value: unknown): value is PlacementMm {
  if (typeof value !== 'object' || value === null) return false;
  const p = value as Record<string, unknown>;
  return (
    isFiniteNumber(p.x) &&
    isFiniteNumber(p.y) &&
    isFinitePositive(p.width) &&
    isFinitePositive(p.height) &&
    isFiniteNumber(p.rotation)
  );
}

/** The box in canvas pixels of a template with the given Pixel Scale (AE8). */
export function placementMmToCanvas(
  placement: PlacementMm,
  pxPerMm: number
): CanvasRectPx {
  return {
    x: placement.x * pxPerMm,
    y: placement.y * pxPerMm,
    width: placement.width * pxPerMm,
    height: placement.height * pxPerMm,
    rotation: placement.rotation,
  };
}

/** The millimetre box of a canvas rectangle on a template (AE8). */
export function canvasToPlacementMm(
  rect: CanvasRectPx,
  pxPerMm: number
): PlacementMm {
  return {
    x: rect.x / pxPerMm,
    y: rect.y / pxPerMm,
    width: rect.width / pxPerMm,
    height: rect.height / pxPerMm,
    rotation: rect.rotation,
  };
}

/**
 * The shape the existing placement rules take, fed with millimetres: the
 * rules are relative and unit-free, so an area whose "pixels" are its
 * millimetres makes every rule work in mm space (R44).
 */
export function placementAreaFromMm(area: PlacementAreaMm): PlacementArea {
  return {
    width: area.widthMm,
    height: area.heightMm,
    maxWidthMm: area.widthMm,
    maxHeightMm: area.heightMm,
    sizeTier: area.sizeTier ?? null,
    sizeTierOverride: area.sizeTierOverride ?? null,
    productId: area.productId ?? null,
    productClass: area.productClass ?? null,
    carryOverPercent: area.carryOverPercent ?? null,
    carryOverAnchor: area.carryOverAnchor ?? null,
    carryOverTopOffsetMm: area.carryOverTopOffsetMm ?? null,
  };
}

/** Fields of a print area row the placement area is built from. */
export interface PrintAreaMmRow extends CarryOverFields {
  maxWidthMm?: number | null;
  maxHeightMm?: number | null;
  sizeTier?: SizeTier | null;
  sizeTierOverride?: SizeTier | null;
}

/**
 * The placement area of a catalogue row, with its effective tier, or null
 * when the row has no usable millimetres (such a position is not
 * configured, R31). With the product, the area carries the product's id,
 * class and the effective carry-over settings (area exception over product
 * value), the same way `placementAreaOf` builds a pixel area.
 */
export function placementAreaMmFromPrintArea(
  row: PrintAreaMmRow | null | undefined,
  product?: PlacementAreaProduct | null
): PlacementAreaMm | null {
  if (
    !row ||
    !isFinitePositive(row.maxWidthMm) ||
    !isFinitePositive(row.maxHeightMm)
  ) {
    return null;
  }
  return {
    widthMm: row.maxWidthMm,
    heightMm: row.maxHeightMm,
    sizeTier: effectiveSizeTier(row),
    sizeTierOverride: row.sizeTierOverride ?? null,
    ...(product ? { productId: product.id } : {}),
    ...effectiveCarryOver(product, row),
  };
}

/** The relative view of a placement on its area, the carry-over's input (AE17). */
export function placementMmToRelative(
  placement: PlacementMm,
  area: PlacementAreaMm
): RelativeDesignState {
  return {
    relCenterX: (placement.x + placement.width / 2) / area.widthMm,
    relCenterY: (placement.y + placement.height / 2) / area.heightMm,
    relWidth: placement.width / area.widthMm,
    designRatio: placement.height / placement.width,
  };
}

/** The placement a relative state describes on an area, the carry-over's output. */
export function relativeToPlacementMm(
  rel: RelativeDesignState,
  area: PlacementAreaMm,
  rotation = 0
): PlacementMm {
  const width = rel.relWidth * area.widthMm;
  const height = width * rel.designRatio;
  return {
    x: rel.relCenterX * area.widthMm - width / 2,
    y: rel.relCenterY * area.heightMm - height / 2,
    width,
    height,
    rotation,
  };
}

/** Whether two placements differ on any edge by more than the tolerance. */
export function placementsMmDiffer(
  a: PlacementMm,
  b: PlacementMm,
  toleranceMm: number = PLACEMENT_MM_TOLERANCE
): boolean {
  return (
    Math.abs(a.x - b.x) > toleranceMm ||
    Math.abs(a.y - b.y) > toleranceMm ||
    Math.abs(a.width - b.width) > toleranceMm ||
    Math.abs(a.height - b.height) > toleranceMm
  );
}
